/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { BarChartCard, type BarDatum } from '../../components/ui/bar-chart.js';
import { DonutChart, type DonutSlice } from '../../components/ui/donut-chart.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import {
  type PaymentMethodSlice,
  type RevenueExpensePoint,
} from '../../lib/api/finance-dashboard.js';
import { useFinanceDashboard } from '../../lib/queries/finance-dashboard.js';
import { formatInr } from '../../lib/utils/money.js';
import { DashboardCard } from '../admin/dashboard-card.js';

const SLICE_COLORS = ['#5b47db', '#10b981', '#f59e0b', '#0ea5e9', '#c084fc', '#94a3b8'];

/** Title-case a snake/lower method code, e.g. `bank_transfer` → `Bank Transfer`. */
function methodLabel(method: string): string {
  return method
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function RevExpProfitCard({
  points,
}: {
  readonly points: readonly RevenueExpensePoint[];
}): JSX.Element {
  const { t } = useTranslation();
  const data = points.map((p) => ({
    label: new Date(p.monthIso).toLocaleString('en', { month: 'short' }),
    revenue: Math.round(p.revenuePaise / 100),
    expenses: Math.round(p.expensesPaise / 100),
    profit: Math.round((p.revenuePaise - p.expensesPaise) / 100),
  }));
  return (
    <DashboardCard
      title={t('finance.analytics.revExpProfit')}
      subtitle={t('finance.analytics.sixMonth')}
    >
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
          <defs>
            <linearGradient id="aRev" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
          <YAxis tick={{ fontSize: 12, fill: '#64748b' }} width={56} />
          <Tooltip />
          <Area
            type="monotone"
            dataKey="revenue"
            name={t('finance.analytics.revenue')}
            stroke="#10b981"
            strokeWidth={2}
            fill="url(#aRev)"
          />
          <Area
            type="monotone"
            dataKey="expenses"
            name={t('finance.analytics.expenses')}
            stroke="#f59e0b"
            strokeWidth={2}
            fillOpacity={0}
          />
          <Area
            type="monotone"
            dataKey="profit"
            name={t('finance.analytics.profit')}
            stroke="#5b47db"
            strokeWidth={2}
            fillOpacity={0}
          />
        </AreaChart>
      </ResponsiveContainer>
    </DashboardCard>
  );
}

function PaymentMethodsCard({
  methods,
}: {
  readonly methods: readonly PaymentMethodSlice[];
}): JSX.Element {
  const { t } = useTranslation();
  const slices: DonutSlice[] = methods.map((m, i) => ({
    label: methodLabel(m.method),
    value: Math.round(m.amountPaise / 100),
    color: SLICE_COLORS[i % SLICE_COLORS.length] ?? '#94a3b8',
  }));
  return (
    <DashboardCard
      title={t('finance.analytics.paymentMethods')}
      subtitle={t('finance.analytics.revenueBySource')}
    >
      {slices.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">{t('finance.analytics.noData')}</p>
      ) : (
        <div>
          <DonutChart slices={slices} />
          <ul className="mt-4 space-y-2">
            {methods.map((m, i) => (
              <li key={m.method} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: SLICE_COLORS[i % SLICE_COLORS.length] ?? '#94a3b8' }}
                    aria-hidden="true"
                  />
                  {methodLabel(m.method)}
                </span>
                <span className="font-medium text-slate-700 dark:text-slate-200">
                  {formatInr(m.amountPaise)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashboardCard>
  );
}

/** Finance → Analytics (Figma). Revenue/expenses/profit, methods, profit trend. */
export function FinanceAnalyticsPage(): JSX.Element {
  const { t } = useTranslation();
  const dashboard = useFinanceDashboard();

  const profitTrend: BarDatum[] = (dashboard.data?.revenueExpenses ?? []).map((p) => ({
    label: new Date(p.monthIso).toLocaleString('en', { month: 'short' }),
    value: Math.round((p.revenuePaise - p.expensesPaise) / 100),
  }));

  return (
    <div className="p-8">
      <PageHeader
        title={t('finance.analytics.heading')}
        subtitle={t('finance.analytics.headingSub')}
      />
      <QueryState
        isLoading={dashboard.isLoading}
        isError={dashboard.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel=""
      >
        {dashboard.data !== undefined && (
          <div className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <RevExpProfitCard points={dashboard.data.revenueExpenses} />
              </div>
              <PaymentMethodsCard methods={dashboard.data.paymentMethods} />
            </div>
            <BarChartCard
              title={t('finance.analytics.monthlyProfit')}
              subtitle={t('finance.analytics.netProfitOverTime')}
              data={profitTrend}
              color="#5b47db"
            />
          </div>
        )}
      </QueryState>
    </div>
  );
}
